import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  IconButton,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { useMutation, useQuery } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import {
  CREATE_SYSTEM_OWNER_INTEGRATION,
  DELETE_INTEGRATION_OWNER_MAPPINGS,
} from "../../../../apis/settingsApis/integration-apis/integrationApis";
import { GET_STAFF_DETAILS } from "../../../../apis/organization-apis/people-apis/peopleApis";
import { GET_EXTERNAL_USERS } from "../../../../apis/organization-apis/people-apis/externalUserApis";

const getInitials = (name, email) => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2)
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return "?";
};

const AVATAR_COLOR = "#1976d2";

const AVATAR_INITIALS_SX = {
  backgroundColor: AVATAR_COLOR,
  color: "#ffffff",
  fontWeight: 600,
};

/**
 * Manage data owners for a single connected integration account.
 * Admins (internal or external) can add/remove owners.
 * Non-admins have read-only access.
 */
const SystemOwnersDialog = ({
  open,
  onClose,
  accountLabel,
  integrationResourceId,
  ownerRows = [],
  currentEmployeeDataId,
  isAdmin,
  onShowSnackbar,
  onOwnersChanged,
}) => {
  const theme = useTheme();
  const [selectedToAdd, setSelectedToAdd] = useState([]);

  const { data: staffData, loading: staffLoading } = useQuery(
    GET_STAFF_DETAILS,
    { skip: !open || !isAdmin, fetchPolicy: "cache-first" }
  );

  const { data: externalData, loading: externalLoading } = useQuery(
    GET_EXTERNAL_USERS,
    {
      variables: { page: 1, limit: 1000 },
      skip: !open || !isAdmin,
      fetchPolicy: "cache-first",
    }
  );

  const [createOwners, { loading: creating }] = useMutation(
    CREATE_SYSTEM_OWNER_INTEGRATION
  );
  const [deleteOwners, { loading: deleting }] = useMutation(
    DELETE_INTEGRATION_OWNER_MAPPINGS
  );

  // Admins (internal or external) can add/remove — including the first owner.
  const canMutate = Boolean(isAdmin);
  const busy = creating || deleting;

  const ownerIdSet = useMemo(() => {
    return new Set(
      (ownerRows || []).map((r) => String(r.system_owner || "").toLowerCase())
    );
  }, [ownerRows]);

  const currentOwnerId = useMemo(
    () => String(currentEmployeeDataId || "").toLowerCase(),
    [currentEmployeeDataId]
  );

  const currentOwnerEmail = useMemo(() => {
    const row = (ownerRows || []).find(
      (r) => String(r.system_owner || "").toLowerCase() === currentOwnerId
    );
    return String(row?.employee_email || "").toLowerCase();
  }, [ownerRows, currentOwnerId]);

  const staffOptions = useMemo(() => {
    const rows = staffData?.getEmployees?.data || [];
    const employeeOptions = rows
      .filter((emp) => {
        if (emp.in_scope === false) return false;
        const roles = Array.isArray(emp.roles) ? emp.roles : [];
        const hasAdminRole = roles.some((role) =>
          String(role?.role_name || "").toLowerCase().includes("admin")
        );
        return hasAdminRole;
      })
      .map((emp) => {
        const primary =
          emp.emails?.find((e) => e.type === "work")?.email ||
          emp.emails?.[0]?.email ||
          "";
        return {
          id: String(emp.employee_data_id || emp.id || ""),
          name:
            emp.name ||
            `${emp.first_name || ""} ${emp.last_name || ""}`.trim(),
          email: primary || emp.work_email || "",
          group: "Employees",
        };
      });

    const extRows = externalData?.getExternalUsers?.data || [];
    const externalOptions = extRows
      .filter(
        (u) =>
          u.is_active !== false &&
          u.is_access_active !== false &&
          String(u.role_name || "").toLowerCase().includes("admin")
      )
      .map((u) => ({
        id: String(u.id || ""),
        name: u.name || u.email || "",
        email: u.email || "",
        group: "External Users",
      }));

    return [...employeeOptions, ...externalOptions].filter(
      (s) =>
        s.id &&
        !ownerIdSet.has(s.id.toLowerCase()) &&
        s.id.toLowerCase() !== currentOwnerId &&
        (!currentOwnerEmail ||
          String(s.email || "").toLowerCase() !== currentOwnerEmail)
    );
  }, [staffData, externalData, ownerIdSet, currentOwnerId, currentOwnerEmail]);

  const handleAdd = async () => {
    if (!integrationResourceId || !selectedToAdd.length) return;
    try {
      const { data } = await createOwners({
        variables: {
          integrationResourceId,
          employeeDataIds: selectedToAdd.map((s) => s.id),
        },
      });
      const res = data?.createSystemOwnerOfAIntegrations;
      if (res?.success) {
        onShowSnackbar?.({
          message: res.message || "Data owners updated.",
          vertical: "top",
          horizontal: "right",
        });
        setSelectedToAdd([]);
        await onOwnersChanged?.();
      } else {
        onShowSnackbar?.({
          message: res?.message || "Could not add data owners.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (e) {
      console.error(e);
      onShowSnackbar?.({
        message: "Could not add data owners.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const handleRemove = async (employeeDataId) => {
    if (!integrationResourceId || !employeeDataId) return;
    try {
      const { data } = await deleteOwners({
        variables: {
          integrationResourceId,
          employeeDataIds: [String(employeeDataId)],
        },
      });
      const res = data?.deleteIntegrationOwnerMappings;
      if (res?.success) {
        onShowSnackbar?.({
          message: res.message || "Owner removed.",
          vertical: "top",
          horizontal: "right",
        });
        await onOwnersChanged?.();
      } else {
        onShowSnackbar?.({
          message: res?.message || "Could not remove owner.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (e) {
      console.error(e);
      onShowSnackbar?.({
        message: "Could not remove owner.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const renderOwnerName = (row) => {
    const name = row.employee_name?.trim();
    const email = row.employee_email?.trim();
    if (name) return name;
    if (email) return email;
    return `User ${row.system_owner?.slice(0, 8) || ""}…`;
  };

  const renderOwnerEmail = (row) => {
    const name = row.employee_name?.trim();
    const email = row.employee_email?.trim();
    if (name && email) return email;
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          pt: 2.5,
          pb: 2,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ color: theme.customColors.text.strong, fontSize: "1rem" }}
          >
            Data Owners
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary, mt: 0.25 }}
          >
            {accountLabel}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{ color: theme.customColors.text.secondary }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {/* Read-only notice for non-admins */}
        {!canMutate && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              mt: 1,
              fontSize: "0.8125rem",
              borderRadius: "8px",
              "& .MuiAlert-icon": { fontSize: 18 },
            }}
          >
            Only administrators can manage data owners.
          </Alert>
        )}

        {/* Current Owners */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            mt: !canMutate ? 0 : 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 700,
              color: theme.customColors.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Current Owners
          </Typography>
          <Chip
            label={ownerRows.length}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6875rem",
              fontWeight: 600,
              borderRadius: 1,
              backgroundColor: theme.customColors.background.light,
              border: `1px solid ${theme.customColors.border.main}`,
              color: theme.customColors.text.secondary,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Box>

        {ownerRows.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              border: `1px dashed ${theme.customColors.border.main}`,
              borderRadius: "8px",
              mb: 2,
            }}
          >
            <GroupOutlinedIcon
              sx={{
                fontSize: 32,
                color: theme.customColors.icons.disabled,
                mb: 1,
              }}
            />
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ color: theme.customColors.text.secondary, mb: 0.5 }}
            >
              No owners assigned
            </Typography>
            {canMutate && (
              <Typography
                variant="caption"
                sx={{ color: theme.customColors.text.secondary }}
              >
                Use the field below to assign the first owner.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {ownerRows.map((row) => {
              const name = renderOwnerName(row);
              const email = renderOwnerEmail(row);
              const initials = getInitials(row.employee_name, row.employee_email);
              const avatarColor = AVATAR_COLOR;
              const isOnlyOwner = ownerRows.length <= 1;

              return (
                <Box
                  key={row.id || `${row.system_owner}-${row.integrated_resource_id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "8px",
                    border: `1px solid ${theme.customColors.border.main}`,
                    backgroundColor: theme.customColors.background.main,
                    transition: "background-color 0.15s",
                    "&:hover": {
                      backgroundColor: theme.customColors.background.hover,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "0.75rem",
                      ...AVATAR_INITIALS_SX,
                      backgroundColor: avatarColor,
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.customColors.text.strong,
                        fontSize: "0.875rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {name}
                    </Typography>
                    {email && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.customColors.text.secondary,
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {email}
                      </Typography>
                    )}
                  </Box>
                  {canMutate && (
                    <Tooltip
                      title={
                        isOnlyOwner
                          ? "At least one owner must remain"
                          : "Remove owner"
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(row.system_owner)}
                          disabled={busy || isOnlyOwner}
                          sx={{
                            color: theme.customColors.text.secondary,
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: theme.customColors.status.error.bg,
                              color: theme.customColors.status.error.text,
                            },
                            "&:disabled": {
                              color: theme.customColors.text.disabled,
                            },
                          }}
                        >
                          <PersonRemoveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Add owners — only for internal users */}
        {canMutate && (
          <>
            <Divider sx={{ borderColor: theme.customColors.border.subtle, mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.5,
              }}
            >
              <PersonAddAlt1OutlinedIcon
                sx={{ fontSize: 15, color: theme.customColors.text.secondary }}
              />
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  color: theme.customColors.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                Add Owners
              </Typography>
            </Box>
            <Autocomplete
              multiple
              options={staffOptions}
              groupBy={(o) => o.group}
              loading={staffLoading || externalLoading}
              filterSelectedOptions
              value={selectedToAdd}
              onChange={(_, v) => setSelectedToAdd(v)}
              getOptionLabel={(o) =>
                o.name
                  ? `${o.name}${o.email ? ` · ${o.email}` : ""}`
                  : o.email || o.id
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disabled={busy}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    py: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.customColors.text.primary,
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    {option.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.customColors.text.secondary,
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    {option.email}
                  </Typography>
                </Box>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name || option.email}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: "8px",
                      border: `1px solid ${theme.customColors.border.main}`,
                      backgroundColor: theme.customColors.background.main,
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search people…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {staffLoading || externalLoading ? (
                          <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#FFFFFF",
                      "&:hover fieldset": {
                        borderColor: theme.customColors.border.strong,
                      },
                    },
                  }}
                />
              )}
            />
          </>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          disabled={busy}
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Close
        </Button>
        {canMutate && (
          <LoadingButton
            variant="contained"
            onClick={handleAdd}
            disabled={!selectedToAdd.length || busy}
            loading={creating}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.875,
              borderRadius: 1,
              color: theme.customColors.button.text,
              "&:hover": {
                bgcolor: theme.customColors.button.primary,
              },
              "&:disabled": {
                bgcolor: theme.customColors.background.light,
                color: theme.customColors.text.disabled,
              },
            }}
          >
            Add selected
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SystemOwnersDialog;
